import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, CheckCircle, AlertCircle } from "lucide-react";

import { apiBaseUrl } from '../../services/config';
const API = apiBaseUrl;
const ATS_LOGIN_URL = "https://ticketing.hirality.com/login";
const rules = [
  { test: p => p.length >= 8,           label:"At least 8 characters" },
  { test: p => /[A-Z]/.test(p),         label:"One uppercase letter"  },
  { test: p => /[a-z]/.test(p),         label:"One lowercase letter"  },
  { test: p => /[0-9]/.test(p),         label:"One number"            },
  { test: p => /[^A-Za-z0-9]/.test(p),  label:"One special character" },
];

export default function InviteSetup() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const token      = params.get("token");
  const type       = params.get("type");

  const [invite,   setInvite]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [form,     setForm]     = useState({ first_name:"", last_name:"", password:"", confirm:"" });
  const [showPw,   setShowPw]   = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [done,     setDone]     = useState(false);

  useEffect(() => {
    const validate = async () => {
      if (!token) { setError("Invalid invite link — no token found."); setLoading(false); return; }
      try {
        const r = await axios.get(`${API}/platform-users/invite/${token}`);
        setInvite(r.data);
        setForm(f => ({ ...f, first_name:r.data.first_name||"", last_name:r.data.last_name||"" }));
      } catch(e) {
        setError(e.response?.data?.detail || "This invite link is invalid or has expired.");
      } finally { setLoading(false); }
    };
    validate();
  }, [token]);

  const strength = rules.filter(r => r.test(form.password)).length;

  const handleSubmit = async () => {
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    if (strength < 4) { setError("Please choose a stronger password"); return; }
    setError(""); setSaving(true);
    try {
      await axios.post(`${API}/platform-users/setup-account`, {
        token, password:form.password,
        first_name:form.first_name, last_name:form.last_name,
      });
      setDone(true);
      // ATS-only company (no HRMS) — send the new user to the ATS app's own
      // login instead of this app's, which they can never actually use.
      const goToAts = invite && invite.hrms_enabled === false && invite.ats_enabled === true;
      setTimeout(() => {
        if (goToAts) window.location.href = ATS_LOGIN_URL;
        else navigate("/login");
      }, 3000);
    } catch(e) {
      setError(e.response?.data?.detail || "Error setting up account. Please try again.");
    } finally { setSaving(false); }
  };

  const ROLE_LABELS = { company_admin:"HR Admin / Company Administrator", ceo:"CEO", cto:"CTO", manager:"Manager", employee:"Employee", senior_manager:"Senior Manager", team_lead:"Team Lead" };

  const inp = (type="text") => ({
    width:"100%", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(0,200,255,0.12)",
    borderRadius:8, color:"#E8F0FE", fontSize:14, padding:"11px 14px", outline:"none",
    fontFamily:"Outfit,sans-serif", boxSizing:"border-box", transition:"border-color 0.15s",
  });

  return (
    <div style={{ minHeight:"100vh", background:"#06101F", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Outfit',sans-serif", padding:20 }}>
      <div style={{ width:"100%", maxWidth:480 }}>
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ width:52, height:52, borderRadius:14, background:"linear-gradient(135deg,#00C8FF,#7C5CFF)", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:24, marginBottom:12 }}>⚡</div>
          <div style={{ fontSize:20, fontWeight:800, color:"#E8F0FE" }}>HRMS Platform</div>
        </div>

        <div style={{ background:"#0D1B2E", border:"1px solid rgba(0,200,255,0.15)", borderRadius:16, overflow:"hidden" }}>
          {loading ? (
            <div style={{ padding:60, textAlign:"center" }}>
              <div style={{ width:36, height:36, border:"2px solid rgba(0,200,255,0.1)", borderTopColor:"#00C8FF", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 14px" }}/>
              <p style={{ color:"#7A93B4", fontSize:14 }}>Validating invite link…</p>
            </div>
          ) : error && !invite ? (
            <div style={{ padding:40, textAlign:"center" }}>
              <AlertCircle size={40} style={{ color:"#FF4D6D", marginBottom:14 }}/>
              <h3 style={{ color:"#E8F0FE", margin:"0 0 10px" }}>Invalid Invite</h3>
              <p style={{ color:"#7A93B4", fontSize:13 }}>{error}</p>
              <button onClick={() => navigate("/login")} style={{ marginTop:16, background:"none", border:"1px solid rgba(0,200,255,0.2)", borderRadius:8, color:"#00C8FF", fontSize:13, padding:"8px 18px", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
                Go to Login
              </button>
            </div>
          ) : done ? (
            <div style={{ padding:48, textAlign:"center" }}>
              <CheckCircle size={48} style={{ color:"#00E5A3", marginBottom:16 }}/>
              <h3 style={{ color:"#00E5A3", margin:"0 0 10px", fontSize:20 }}>Account Ready!</h3>
              <p style={{ color:"#7A93B4", fontSize:13 }}>
                {invite && invite.hrms_enabled === false && invite.ats_enabled === true
                  ? "Redirecting to ATS login in 3 seconds…"
                  : "Redirecting to login in 3 seconds…"}
              </p>
            </div>
          ) : invite && (
            <>
              {/* Header */}
              <div style={{ padding:"24px 28px", background:"linear-gradient(135deg,rgba(0,200,255,0.1),rgba(124,92,255,0.1))", borderBottom:"1px solid rgba(0,200,255,0.1)" }}>
                <div style={{ fontSize:11, color:"#00C8FF", fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:6 }}>
                  {invite.company_name || "HRMS Platform"}
                </div>
                <h2 style={{ margin:"0 0 6px", fontSize:20, fontWeight:800, color:"#E8F0FE" }}>
                  {type === "company" ? "Set up your company account" : `Welcome, ${invite.first_name || ""}!`}
                </h2>
                <div style={{ fontSize:13, color:"#7A93B4", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11.5, padding:"2px 8px", borderRadius:10, background:"rgba(0,200,255,0.1)", color:"#00C8FF", fontWeight:600 }}>
                    {ROLE_LABELS[invite.role] || invite.role}
                  </span>
                  <span>·</span>
                  <span>{invite.email}</span>
                </div>
              </div>

              {/* Form */}
              <div style={{ padding:28, display:"flex", flexDirection:"column", gap:16 }}>
                {error && (
                  <div style={{ background:"rgba(255,77,109,0.1)", border:"1px solid rgba(255,77,109,0.25)", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#FF4D6D", display:"flex", alignItems:"center", gap:8 }}>
                    <AlertCircle size={14}/>{error}
                  </div>
                )}

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  {["first_name","last_name"].map(f => (
                    <div key={f}>
                      <label style={{ fontSize:11.5, color:"#7A93B4", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>{f==="first_name"?"First Name":"Last Name"}</label>
                      <input value={form[f]} onChange={e=>setForm(p=>({...p,[f]:e.target.value}))} placeholder={f==="first_name"?"First":"Last"} style={inp()}/>
                    </div>
                  ))}
                </div>

                <div>
                  <label style={{ fontSize:11.5, color:"#7A93B4", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Create Password *</label>
                  <div style={{ position:"relative" }}>
                    <input type={showPw?"text":"password"} value={form.password}
                      onChange={e=>setForm(p=>({...p,password:e.target.value}))}
                      placeholder="Enter a strong password" style={{ ...inp(), paddingRight:44 }}/>
                    <button onClick={() => setShowPw(p=>!p)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#7A93B4" }}>
                      {showPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                  {/* Password strength */}
                  {form.password && (
                    <>
                      <div style={{ display:"flex", gap:4, marginTop:8 }}>
                        {[1,2,3,4,5].map(i => (
                          <div key={i} style={{ flex:1, height:3, borderRadius:3, background: strength>=i ? (strength>=5?"#00E5A3":strength>=3?"#FFB800":"#FF4D6D") : "rgba(255,255,255,0.08)" }}/>
                        ))}
                      </div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
                        {rules.map((r,i) => (
                          <span key={i} style={{ fontSize:11, padding:"2px 7px", borderRadius:10, background:r.test(form.password)?"rgba(0,229,163,0.1)":"rgba(255,255,255,0.05)", color:r.test(form.password)?"#00E5A3":"#7A93B4", display:"flex", alignItems:"center", gap:4 }}>
                            {r.test(form.password)?"✓":""} {r.label}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label style={{ fontSize:11.5, color:"#7A93B4", display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Confirm Password *</label>
                  <input type="password" value={form.confirm}
                    onChange={e=>setForm(p=>({...p,confirm:e.target.value}))}
                    placeholder="Repeat your password"
                    style={{ ...inp(), borderColor: form.confirm && form.confirm!==form.password?"#FF4D6D":"rgba(0,200,255,0.12)" }}/>
                  {form.confirm && form.confirm!==form.password && <p style={{ color:"#FF4D6D", fontSize:11.5, marginTop:4 }}>Passwords do not match</p>}
                </div>

                <button onClick={handleSubmit} disabled={saving || !form.password || form.password!==form.confirm}
                  style={{ width:"100%", background:"linear-gradient(135deg,#00C8FF,#7C5CFF)", border:"none", borderRadius:8, color:"#fff", fontFamily:"Outfit,sans-serif", fontSize:14, fontWeight:700, padding:13, cursor:saving?"not-allowed":"pointer", opacity:(saving||!form.password||form.password!==form.confirm)?0.6:1, transition:"opacity 0.2s" }}>
                  {saving ? "Setting up account…" : "Activate Account →"}
                </button>
              </div>
            </>
          )}
        </div>

        <p style={{ textAlign:"center", fontSize:12, color:"#3D5473", marginTop:16 }}>
          Already have an account? <a href="/login" style={{ color:"#00C8FF", textDecoration:"none" }}>Log in</a>
        </p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
